import { CardGrid } from '@liveauctioneers/hammer-ui-core/card/cardGrid';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { getItemSummaryMap } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getPagePosition } from './components/SearchPageItemCard/helpers/getPagePosition';
import { getSearchedAndPromotedItems } from '@/redux/modules/search/aggregateResults/searchAggregate.selectors';
import { getSearchPageSize } from '@/redux/modules/pagination';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import { GlobalState } from '@/redux/store';
import { isInitialRenderWithResultsAtom } from '@/pages/SearchPageSimplified/atoms/searchPageAtoms';
import { ItemSummary } from '@/types/item/ItemSummary';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { SearchPageItemCard } from './components/SearchPageItemCard/SearchPageItemCard';
import { trackPromotedItemVisible } from '@/redux/modules/analytics';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useAtomValue } from 'jotai';
import { useSearchParams } from 'react-router-dom';
import useGetSearchColumns from '@/hooks/useGetSearchColumns';

const DEFAULT_PAGE_NUMBER = 1;

type Props = {
    adComponent?: ReactNode;
    splitAtIndex?: number;
};

export const SearchPageCardGrid = ({ adComponent, splitAtIndex }: Props = {}) => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const page = Number(searchParams.get('page')) || DEFAULT_PAGE_NUMBER;

    const columns = useGetSearchColumns({ isOnSearchPage: true });
    const pageSize = useAppSelector(getSearchPageSize);
    const isInitialRenderWithResults = useAtomValue(isInitialRenderWithResultsAtom);
    const searchPageItemIds = useAppSelector((state: GlobalState) =>
        // Prevent duplicate items from being rendered
        Array.from(new Set(getSearchedAndPromotedItems(state, columns)))
    );

    const getItemPagePosition = useCallback(
        (itemId: number) => getPagePosition(itemId, page, pageSize, searchPageItemIds),
        [page, pageSize, searchPageItemIds]
    );

    const trackVisible = useCallback(
        (itemData: ItemSummary & { position: number }) => {
            if (itemData.promoted) {
                dispatch(trackPromotedItemVisible({ itemId: itemData.itemId }));
            }
        },
        [dispatch]
    );

    // Initialize transition state - useEffect will handle showing cards when data is ready
    const [startTransition, setStartTransition] = useState(false);

    const itemCardData = useAppSelector((state) => getItemSummaryMap(state, searchPageItemIds));

    const itemCardDataWithPosition = useMemo(() => {
        const result: Record<number, ItemSummary & { position: number }> = {};

        searchPageItemIds.forEach((itemId) => {
            const itemData = itemCardData[itemId];
            if (itemData) {
                result[itemId] = {
                    ...itemData,
                    position: getItemPagePosition(itemId),
                };
            }
        });

        return result;
    }, [itemCardData, searchPageItemIds, getItemPagePosition]);

    const { isInitialLoading, submitted: isLoadingSearchResults } = useAppSelector((state) => getSearchUiState(state));

    const itemCardDataLength = useMemo(() => Object.keys(itemCardData).length, [itemCardData]);

    const forceSkeleton = useMemo(() => {
        const hasData = searchPageItemIds.length > 0 && itemCardDataLength > 0;
        const isLoading = isLoadingSearchResults || isInitialLoading || (isInitialRenderWithResults && !hasData);

        return isLoading;
    }, [
        isLoadingSearchResults,
        isInitialLoading,
        isInitialRenderWithResults,
        searchPageItemIds.length,
        itemCardDataLength,
    ]);

    // Handle transition state in useEffect to avoid SSR hydration mismatches
    useEffect(() => {
        if (!forceSkeleton) {
            requestAnimationFrame(() => {
                setStartTransition(true);
            });
        } else {
            setStartTransition(false);
        }
    }, [forceSkeleton]);

    // Common CardGrid props
    const cardGridProps = {
        card: SearchPageItemCard,
        className: cn(
            'min-w-0 gap-y-lg mobile:grid-cols-2 mobile:gap-y-md [&>article]:transition-opacity [&>article]:duration-[300ms] [&>article]:ease-in',
            {
                '[&>article]:opacity-0': !startTransition,
                '[&>article]:opacity-100': startTransition,
            }
        ),
        colsByBreakpoint: {
            lg: 4,
            md: 4,
            sm: 3,
            xl: 4,
            xs: 2,
        },
        forceSkeleton,
        gapsByBreakpoint: {
            lg: 'md',
            md: 'md',
            sm: 'sm',
            xl: 'md',
            xs: 'sm',
        } as const,
        onCardVisible: trackVisible,
    };

    // If ad props are provided, split the grid and insert the ad
    if (adComponent && splitAtIndex) {
        const firstBatchIds = searchPageItemIds.slice(0, splitAtIndex);
        const secondBatchIds = searchPageItemIds.slice(splitAtIndex);

        return (
            <>
                {/* First batch: rows 1-5 */}
                <CardGrid
                    {...cardGridProps}
                    cardData={itemCardDataWithPosition}
                    cards={firstBatchIds}
                    skeletonCardCount={splitAtIndex}
                />

                {adComponent}

                {/* Second batch: remaining items */}
                {secondBatchIds.length > 0 && (
                    <CardGrid
                        {...cardGridProps}
                        cardData={itemCardDataWithPosition}
                        cards={secondBatchIds}
                        skeletonCardCount={pageSize - splitAtIndex}
                    />
                )}
            </>
        );
    }

    // Normal render without ad
    return (
        <CardGrid
            {...cardGridProps}
            cardData={itemCardDataWithPosition}
            cards={searchPageItemIds}
            skeletonCardCount={pageSize}
        />
    );
};
